import React, { Dispatch, useEffect } from "react";

import SBCLinkCell from "ComparePlans/Components/SBCLinkCell";
import { requestMecPlans, requestMvpPlans, setLimitedValuePlansFilter } from "QuotePage/Actions";
import DropdownMultiSelectFilter from "QuotePage/app/compactQuotePage/plansFilterPanel/plansFilterPanelDumb/DropdownMultiSelectFilter";
import { limitedCoveragePlanTypes, EMPTY_FILTERS } from "QuotePage/app/compactQuotePage/shared/FilterData";
import { getMecPlans, getCheapestSmallGroupPlan, getMvpPlans, getLimitedValuePlansFilterConfig, getPayrollLead } from "QuotePage/Selectors";
import { connect, useDispatch } from "react-redux";
import formatCurrency, { notCoveredOrFormatCurrency } from "Utils/formatCurrency";
import { MedicalPlan } from "Utils/types";

import { Body2 } from "Cobalt/BodyText";
import { LinkButton } from "Cobalt/Button";
import { Callout } from "Cobalt/Callout";
import { CarrierLogo } from "Cobalt/CarrierLogo";
import { Title } from "Cobalt/Header";
import { StandaloneTrigger, Modal, ModalBody, ModalHeader, ModalFooter } from "Cobalt/Modal";
import { Table, HeaderTR, TH, TR, TD } from "Cobalt/TableV2";
import BodyText from "Common/BodyText";
import Button from "Common/Button";
import { Card, CardSection } from "Common/Card";
import { addPackageToCart } from "Common/CartService";
import { useFlipper } from "Common/flipperUtils";
import { Col, Row } from "Common/Grid";
import { Div, Span } from "Common/HTML";

import NoPlansCard from "./compactQuotePage/NoPlansCard";

import makeBem from "Utils/makeBem";

const bem = makeBem("MECQuotePage");

const MECInfoTable = ({ plan, cheapestSmallGroupPlan }) => (
  <>
    <Table mt={3}>
      <thead>
        <HeaderTR>
          <TH>Standard Medical</TH>
          <TH />
        </HeaderTR>
      </thead>
      <tbody>
        <TR>
          <TD>Cost</TD>
          <TD>Age based, starting at {formatCurrency(cheapestSmallGroupPlan?.average_monthly_premium)}</TD>
        </TR>
        <TR>
          <TD>Benefits</TD>
          <TD>Covers emergencies, specialty care, unlimited visits</TD>
        </TR>
        <TR>
          <TD>Funding</TD>
          <TD>Insurance company pays for claims</TD>
        </TR>

        <TR>
          <TD>Administration</TD>
          <TD>Carrier administers all changes and claims</TD>
        </TR>
      </tbody>
    </Table>
    <Table mt={5} mb={3}>
      <thead>
        <HeaderTR>
          <TH>Minimum Essential Coverage Plans</TH>
          <TH />
        </HeaderTR>
      </thead>
      <tbody>
        <TR>
          <TD>Cost</TD>
          <TD>Fixed rates, starting at {formatCurrency(plan?.average_monthly_premium)}</TD>
        </TR>
        <TR>
          <TD>Benefits</TD>
          <TD>Coverage limited to routine care, limits on visits</TD>
        </TR>
        <TR>
          <TD>Administration</TD>
          <TD>Company and employees have to administer claims</TD>
        </TR>
      </tbody>
    </Table>
    <Table mt={5} mb={3}>
      <thead>
        <HeaderTR>
          <TH>Minimum Value Plans</TH>
          <TH />
        </HeaderTR>
      </thead>
      <tbody>
        <TR>
          <TD>Cost</TD>
          <TD>Fixed rates, starting at {formatCurrency(plan?.average_monthly_premium)}</TD>
        </TR>
        <TR>
          <TD>Benefits</TD>
          <TD>No coverage for some major services like surgeries</TD>
        </TR>
        <TR>
          <TD>Administration</TD>
          <TD>Company and employees have to administer claims</TD>
        </TR>
      </tbody>
    </Table>
  </>
);

const MECInfoModalTrigger = ({ plan, cheapestSmallGroupPlan }) => (
  <StandaloneTrigger
    trigger={(props) => (
      <a href="#" {...props}>
        View Comparison
      </a>
    )}
    mt={3}
    modal={(props) => (
      <Modal {...props}>
        <ModalHeader closeButton>
          <Title>What are Minimum Essential Coverage and Minimal Value Plans?</Title>
        </ModalHeader>
        <ModalBody>
          <MECInfoTable plan={plan} cheapestSmallGroupPlan={cheapestSmallGroupPlan} />
        </ModalBody>
        <ModalFooter>
          <Button key="close" onClick={() => props.onHide()}>
            Done
          </Button>
        </ModalFooter>
      </Modal>
    )}
  />
);

const StartApplicationButton = ({ payrollLead, plan, handleSelectPlan }) => {
  if (payrollLead?.partner) {
    return <LinkButton href={`/${payrollLead.partner}/router/payroll-prospect`}>Add Plan</LinkButton>;
  }
  return <Button onClick={() => handleSelectPlan(plan.id)}>Add Plan</Button>;
};

const MECCallout = ({ plan, cheapestSmallGroupPlan }) => {
  const bodyCopy =
    "Please read the terms carefully as these plans offer coverage \n" +
    'that is considered "minimal", so is less than what a traditional \n' +
    "plan would typically cover. These plans are also self-funded, \n" +
    "meaning your company will pay for employees' healthcare claims.";

  return (
    <Callout
      type="info"
      title="Minimum Essential Coverage and Minimum Value Plans"
      body={
        <>
          <Body2 mb={3}>{bodyCopy}</Body2>
          <MECInfoModalTrigger plan={plan} cheapestSmallGroupPlan={cheapestSmallGroupPlan} />
        </>
      }
      showIcon
      className={bem("MECCallout")}
      mb={4}
    />
  );
};

const appendPlanTypeToPlanName = (plan) => {
  let planName = plan.display_name;
  if (plan.target_market === "mec") {
    if (!planName.includes("MEC")) {
      planName = `${planName} MEC`;
    }
    return `${planName.replace("MEC", "Minimum Essential Coverage")}`;
  }
  if (plan.target_market === "mvp") {
    return `${planName} Minimum Value Plan`;
  }
};

const detailRow = (name: string, value: string) => (
  <Div key={name} className={bem("MECPlanCard", "Detail")} mb={1}>
    <BodyText weight="bold">{name}:</BodyText>
    <BodyText>{value}</BodyText>
  </Div>
);

export const MECPlanCard = ({ plan, handleSelectPlan, payrollLead }) => {
  const detailsColumnOne = {
    Deductible: formatCurrency(plan.deductible),
    "Out of Pocket Maximum": plan.limit == 999999 ? "Unlimited" : formatCurrency(plan.limit),
    "Preventative Care Visits": "$0 Copay",
    "Preventative Care Prescriptions": "$0 Copay",
  };

  const drugTier1 = notCoveredOrFormatCurrency(plan.estimated_costs["drug_tier:Tier 1"]);

  const drugTier4 = notCoveredOrFormatCurrency(plan.estimated_costs["drug_tier:Tier 4"]);

  const detailsColumnTwo = {
    "Primary Care Visits": notCoveredOrFormatCurrency(plan.estimated_costs.pcp_visit),

    "Specialist Visits": notCoveredOrFormatCurrency(plan.estimated_costs.specialist_visit),

    "Urgent Care": notCoveredOrFormatCurrency(plan.estimated_costs.urgent_care),
    "Other Prescription Coverage": drugTier1 == "Not Covered" ? drugTier1 : `${drugTier1} - ${drugTier4}`,
    "ER and Hospital": notCoveredOrFormatCurrency(plan.estimated_costs.emergency_room),
  };

  return (
    <Card className={bem("MECPlanCard")}>
      <CardSection className={bem("MECPlanCard", "Header")}>
        <Span>
          <CarrierLogo plan={plan} />
        </Span>
        <Span>
          <BodyText size="large" pl={5} weight="bold">
            {appendPlanTypeToPlanName(plan)}
          </BodyText>
        </Span>
        <Span>
          <BodyText size="large" weight="bold">
            {formatCurrency(plan.average_monthly_premium)}/mo
          </BodyText>
          <BodyText muted>per employee</BodyText>
        </Span>
      </CardSection>
      <CardSection>
        <Row>
          <Col xs={6} md={5}>
            {Object.entries(detailsColumnOne).map(([name, value]) => detailRow(name, value))}
          </Col>
          <Col xs={0} md={2} />
          <Col xs={6} md={5}>
            {Object.entries(detailsColumnTwo).map(([name, value]) => detailRow(name, value))}
          </Col>
        </Row>
        <Row pt={4}>
          <Col xs={6} md={7} />
          <Col className={bem("MECPlanCard", "Detail")} xs={6} md={5}>
            <Div pt={2}>
              <SBCLinkCell plan={plan} />
            </Div>
            <Div>
              <StartApplicationButton handleSelectPlan={handleSelectPlan} plan={plan} payrollLead={payrollLead} />
            </Div>
          </Col>
        </Row>
      </CardSection>
    </Card>
  );
};

type Props = {
  mecPlans: Array<MedicalPlan>;
  mvpPlans: Array<MedicalPlan>;
  cheapestSmallGroupPlan: MedicalPlan;
  updateCart: (_arg0: any) => void;
  filterConfig: any;
  displayedPlanTypes: {};
};

export const MECPlansDumb = ({ mecPlans, cheapestSmallGroupPlan, updateCart, mvpPlans, filterConfig, payrollLead }: Props) => {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(requestMecPlans({}));
    dispatch(requestMvpPlans({}));
  }, []);

  const onChangeFilter = (filterKey, value) => {
    dispatch(setLimitedValuePlansFilter(filterKey, value));
  };

  const displayedPlanTypes = filterConfig.plan_type;

  const displayMecPlans = displayedPlanTypes.include?.includes("mec_plans");
  const displayMvpPlans = displayedPlanTypes.include?.includes("mvp_plans");

  const displayedPlans = [...(displayMecPlans ? mecPlans : []), ...(displayMvpPlans ? mvpPlans : [])].sort(
    (a, b) => a.total_employee_premium - b.total_employee_premium,
  );

  return (
    <>
      <Row mb={4} className="flex justify-end">
        <Col sm={2}>
          <DropdownMultiSelectFilter
            options={limitedCoveragePlanTypes}
            defaultOptions={limitedCoveragePlanTypes.map((x) => x.value)}
            defaultSelect
            filterConfig={filterConfig}
            filterKey="plan_type"
            menuTitle="Plan Type"
            onChangeFilter={onChangeFilter}
          />
        </Col>
      </Row>
      <MECCallout plan={mecPlans[0] || {}} cheapestSmallGroupPlan={cheapestSmallGroupPlan} />
      {displayedPlans.map((plan) => (
        <MECPlanCard
          key={plan.id}
          plan={plan}
          payrollLead={payrollLead}
          handleSelectPlan={(planId) => {
            updateCart([planId]);
          }}
        />
      ))}
      {displayedPlans.length === 0 ? <NoPlansCard /> : null}
    </>
  );
};

const mapStateToProps = (state) => ({
  payrollLead: getPayrollLead(state),
  mecPlans: getMecPlans(state).sort((a, b) => a.total_employee_premium - b.total_employee_premium),
  mvpPlans: getMvpPlans(state).sort((a, b) => a.total_employee_premium - b.total_employee_premium),
  cheapestSmallGroupPlan: getCheapestSmallGroupPlan(state),
  filterConfig: getLimitedValuePlansFilterConfig(state) || EMPTY_FILTERS.limitedValuePlansFilter,
});

const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
  updateCart: (planIds) => {
    dispatch(addPackageToCart(planIds));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(MECPlansDumb);
